import { Grid, Typography } from '@mui/material';
import { get, range } from 'lodash';
import { useEffect } from 'react';
import { type Path } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { ColorButtonGroup } from 'src/components/form/colorButtonGroup/ColorButtonGroup';
import {
  ColorPickerWidget,
  InputWidget,
  NumberInputWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import {
  injectDefaultFontIntoHtml,
  QuillTheme,
  RichTextEditor,
  RichTextToolbarOptions,
} from 'src/components/widget/richTextWidget';
import { FONTS_OPTIONS } from 'src/constants';
import { useGetProjectConfig, useRteDefaultStyleValues } from 'src/hooks';
import { lang, theme } from 'src/utils';

import {
  type ConfirmationUSConfigInterface,
  BackgroundColor,
} from '../CODForm.interface';
import { OrderConfirmTitleProps } from './OrderConfirmTitle.interface';

export const OrderConfirmTitle = (props: OrderConfirmTitleProps) => {
  const { data: projectConfig } = useGetProjectConfig();

  const handleFieldChange =
    (field: Path<ConfirmationUSConfigInterface>) => (value: any) => {
      if (value !== null) {
        props.handleOnChange(`codSettings.confirmSetting.${field}`, value);
      }
    };
  const showConfirmSettingRmhcMessage = get(
    props.confirmSetting,
    'topMessage.hideRmhcMessage',
    false,
  );

  const hiddenToolbarItems: RichTextToolbarOptions[] = [
    RichTextToolbarOptions.STRIKE,
  ];

  const disabled = false;
  const projectFonts = projectConfig?.fonts?.map((item) => item.name) ?? [];
  const mappedFonts = projectFonts.length > 0 ? projectFonts : FONTS_OPTIONS;

  const rteDefaultStyleValues = useRteDefaultStyleValues(
    props.theme,
    projectConfig,
  );

  useEffect(() => {
    if (!rteDefaultStyleValues.fontFamily) return;
    const message = get(props.confirmSetting, 'topMessage.message', '');
    if (!message) return;
    // Seed font only — never inject color, so the message inherits the
    // section's container color via the GCF cascade (see CC-22307).
    const styled = injectDefaultFontIntoHtml(message, {
      fontFamily: rteDefaultStyleValues.fontFamily,
    });
    if (styled !== message) {
      handleFieldChange('topMessage.message')(styled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rteDefaultStyleValues.fontFamily]);

  const options = {
    placeholder: 'Enter text...',
    richTextTheme: QuillTheme.TOOLBAR,
  };
  const donationLabel = (get(props.confirmSetting, 'total.donate.label', '') ??
    '') as string;

  const titleItems = [
    {
      label: 'Your order background',
      hasError: !!props.errors?.total?.background,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <ColorButtonGroup
              label=""
              value={get(props.confirmSetting, 'total.background')}
              onChange={handleFieldChange('total.background')}
              colorOptions={[
                {
                  value: BackgroundColor.BLACK,
                  shadeValue: BackgroundColor.BLACK,
                  colorTitle: 'Promo black',
                  colorValue: '#000000 to #434040',
                },
                {
                  value: BackgroundColor.GOLD,
                  shadeValue: BackgroundColor.GOLD,
                  colorTitle: 'Promo gold',
                  colorValue: '#FFBC0D to #FFDF1E',
                },
              ]}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Your order',
      hasError: !!props.errors?.total?.title,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <InputWidget
              label="Your order label"
              value={get(props.confirmSetting, 'total.title.label')}
              onChange={handleFieldChange('total.title.label')}
              hasError={!!props.errors?.total?.title}
              error={props.errors?.total?.title?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Font size"
              value={get(props.confirmSetting, 'total.title.fontSize')}
              onChange={handleFieldChange('total.title.fontSize')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.title?.fontSize}
              error={props.errors?.total?.title?.fontSize?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Line height"
              value={get(props.confirmSetting, 'total.title.lineHeight')}
              onChange={handleFieldChange('total.title.lineHeight')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.title?.lineHeight}
              error={props.errors?.total?.title?.lineHeight?.message}
            />
          </Grid>
          <Grid item xs={2} container alignItems="flex-end">
            <ColorPickerWidget
              value={get(props.confirmSetting, 'total.title.color')}
              onChange={handleFieldChange('total.title.color')}
              hasError={!!props.errors?.total?.title?.color}
              error={props.errors?.total?.title?.color?.message}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Total label',
      hasError: !!props.errors?.total?.total,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <InputWidget
              label="Total label"
              value={get(props.confirmSetting, 'total.total.label')}
              onChange={handleFieldChange('total.total.label')}
              hasError={!!props.errors?.total?.total}
              error={props.errors?.total?.total?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Font size"
              value={get(props.confirmSetting, 'total.total.fontSize')}
              onChange={handleFieldChange('total.total.fontSize')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.total?.fontSize}
              error={props.errors?.total?.total?.fontSize?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Line height"
              value={get(props.confirmSetting, 'total.total.lineHeight')}
              onChange={handleFieldChange('total.total.lineHeight')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.total?.lineHeight}
              error={props.errors?.total?.total?.lineHeight?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Letter spacing"
              value={get(props.confirmSetting, 'total.total.letterSpacing')}
              onChange={handleFieldChange('total.total.letterSpacing')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.total?.letterSpacing}
              error={props.errors?.total?.total?.letterSpacing?.message}
            />
          </Grid>
          <Grid item xs={2} container alignItems="flex-end">
            <ColorPickerWidget
              value={get(props.confirmSetting, 'total.total.color')}
              onChange={handleFieldChange('total.total.color')}
              hasError={!!props.errors?.total?.total?.color}
              error={props.errors?.total?.total?.color?.message}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Savings label',
      hasError: !!props.errors?.total?.save,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <InputWidget
              label="Savings label"
              value={get(props.confirmSetting, 'total.save.label')}
              onChange={handleFieldChange('total.save.label')}
              hasError={!!props.errors?.total?.save}
              error={props.errors?.total?.save?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Font size"
              value={get(props.confirmSetting, 'total.save.fontSize')}
              onChange={handleFieldChange('total.save.fontSize')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.save?.fontSize}
              error={props.errors?.total?.save?.fontSize?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Line height"
              value={get(props.confirmSetting, 'total.save.lineHeight')}
              onChange={handleFieldChange('total.save.lineHeight')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.save?.lineHeight}
              error={props.errors?.total?.save?.lineHeight?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Letter spacing"
              value={get(props.confirmSetting, 'total.save.letterSpacing')}
              onChange={handleFieldChange('total.save.letterSpacing')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.save?.letterSpacing}
              error={props.errors?.total?.save?.letterSpacing?.message}
            />
          </Grid>
          <Grid item xs={2} container alignItems="flex-end">
            <ColorPickerWidget
              value={get(props.confirmSetting, 'total.save.color')}
              onChange={handleFieldChange('total.save.color')}
              hasError={!!props.errors?.total?.save?.color}
              error={props.errors?.total?.save?.color?.message}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Tax label',
      hasError: !!props.errors?.total?.tax,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <InputWidget
              label="Tax label"
              value={get(props.confirmSetting, 'total.tax.label')}
              onChange={handleFieldChange('total.tax.label')}
              hasError={!!props.errors?.total?.tax}
              error={props.errors?.total?.tax?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Font size"
              value={get(props.confirmSetting, 'total.tax.fontSize')}
              onChange={handleFieldChange('total.tax.fontSize')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.tax?.fontSize}
              error={props.errors?.total?.tax?.fontSize?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Line height"
              value={get(props.confirmSetting, 'total.tax.lineHeight')}
              onChange={handleFieldChange('total.tax.lineHeight')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.tax?.lineHeight}
              error={props.errors?.total?.tax?.lineHeight?.message}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInputWidget
              label="Letter spacing"
              value={get(props.confirmSetting, 'total.tax.letterSpacing')}
              onChange={handleFieldChange('total.tax.letterSpacing')}
              options={{ suffix: 'px' }}
              hasError={!!props.errors?.total?.tax?.letterSpacing}
              error={props.errors?.total?.tax?.letterSpacing?.message}
            />
          </Grid>
          <Grid item xs={2} container alignItems="flex-end">
            <ColorPickerWidget
              value={get(props.confirmSetting, 'total.tax.color')}
              onChange={handleFieldChange('total.tax.color')}
              hasError={!!props.errors?.total?.tax?.color}
              error={props.errors?.total?.tax?.color?.message}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Additional labels',
      hasError: !!props.errors?.body?.discount,
      content: (
        <Grid container spacing={2} padding={3}>
          <Grid item xs={12}>
            <SwitchButtonWidget
              label="Order Messaging"
              value={!showConfirmSettingRmhcMessage}
              onChange={() =>
                handleFieldChange('topMessage.hideRmhcMessage')(
                  !showConfirmSettingRmhcMessage,
                )
              }
            />
          </Grid>
          {!showConfirmSettingRmhcMessage && (
            <Grid item xs={12}>
              <RichTextEditor
                placeholder={options?.placeholder || ''}
                value={get(props.confirmSetting, 'topMessage.message')}
                onChange={handleFieldChange('topMessage.message')}
                disabled={disabled}
                theme={options?.richTextTheme}
                fontFamily={mappedFonts}
                fontSize={range(8, 100).map((size) => `${size}px`)}
                letterSpacing={['0px', '1px', '2px', '3px', '4px', '5px']}
                hide={hiddenToolbarItems}
                defaultStyleValues={{
                  fontFamily: rteDefaultStyleValues.fontFamily,
                }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <InputWidget
              label="Deal label"
              value={get(props.confirmSetting, 'body.discount.dealUsed.label')}
              onChange={handleFieldChange('body.discount.dealUsed.label')}
              hasError={!!props.errors?.body?.discount?.dealUsed?.label}
              error={props.errors?.body?.discount?.dealUsed?.label?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <InputWidget
              label="Reward label"
              value={get(props.confirmSetting, 'body.discount.reward.label')}
              onChange={handleFieldChange('body.discount.reward.label')}
              hasError={!!props.errors?.body?.discount?.reward?.label}
              error={props.errors?.body?.discount?.reward?.label?.message}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: lang.overrides.displayRMHCMessage,
      hasError:
        props.showValidationErrors &&
        (!!props.errors?.topMessage?.message ||
          !!props.errors?.total?.donate ||
          !donationLabel?.trim()),
      content: (
        <Grid container spacing={2} padding={3}>
          <>
            <Grid item xs={12}>
              {(() => {
                const donateHasError =
                  (props.showValidationErrors && !donationLabel?.trim()) ||
                  !!props.errors?.total?.donate?.label;

                return (
                  <>
                    <Typography
                      sx={{
                        color: donateHasError
                          ? theme.status.danger.dark
                          : '#000',
                        fontWeight: 400,
                      }}>
                      {lang.donationLabel}
                    </Typography>
                    <InputWidget
                      label=""
                      value={donationLabel}
                      onChange={handleFieldChange('total.donate.label')}
                      hasError={!!props.errors?.total?.donate?.label}
                      error={props.errors?.total?.donate?.label?.message}
                    />
                  </>
                );
              })()}
            </Grid>
            <Grid item xs={6}>
              <NumberInputWidget
                label="Font size"
                value={get(props.confirmSetting, 'total.donate.fontSize')}
                onChange={handleFieldChange('total.donate.fontSize')}
                options={{ suffix: 'px' }}
                hasError={!!props.errors?.total?.donate?.fontSize}
                error={props.errors?.total?.donate?.fontSize?.message}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberInputWidget
                label="Line height"
                value={get(props.confirmSetting, 'total.donate.lineHeight')}
                onChange={handleFieldChange('total.donate.lineHeight')}
                options={{ suffix: 'px' }}
                hasError={!!props.errors?.total?.donate?.lineHeight}
                error={props.errors?.total?.donate?.lineHeight?.message}
              />
            </Grid>
            <Grid item xs={6} container alignItems="flex-end">
              <ColorPickerWidget
                value={get(props.confirmSetting, 'total.donate.color')}
                onChange={handleFieldChange('total.donate.color')}
                hasError={!!props.errors?.total?.donate?.color}
                error={props.errors?.total?.donate?.color?.message}
              />
            </Grid>
            <Grid item xs={6}>
              <NumberInputWidget
                label="Letter spacing"
                value={get(props.confirmSetting, 'total.donate.letterSpacing')}
                onChange={handleFieldChange('total.donate.letterSpacing')}
                options={{ suffix: 'px' }}
                hasError={!!props.errors?.total?.donate?.letterSpacing}
                error={props.errors?.total?.donate?.letterSpacing?.message}
              />
            </Grid>
          </>
        </Grid>
      ),
    },
  ];

  return <SidebarAccordion items={titleItems} />;
};
